import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { AlertTriangle, Bug, Calendar, ChevronRight, ChevronLeft, Sparkles, Info, Thermometer, Clock, CheckCircle2, ChevronDown, Shield, BookOpen, Wrench, ListChecks, BellRing } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { sendOrQueue, queuedToast } from '../utils/offlineStore';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = ['mite_type', 'treatment', 'hives', 'schedule', 'review'];

const TreatmentPlanWizard = ({ open, onClose, token, hives = [], yards = [], onSuccess, initialContext }) => {
  const [step, setStep] = useState(0);
  const [treatmentTypes, setTreatmentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [scanContext, setScanContext] = useState(null);
  const [rotationRec, setRotationRec] = useState(null);
  
  const [formData, setFormData] = useState({
    mite_type: 'varroa',
    treatment_type_id: '',
    name: '',
    description: '',
    hive_ids: [],
    hive_numbers: [],
    yard_id: '',
    yard_name: '',
    start_date: new Date().toISOString().split('T')[0],
    pre_treatment_mite_count: '',
    pre_treatment_bee_sample: '300',
    notes: ''
  });

  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [activeTreatmentHiveIds, setActiveTreatmentHiveIds] = useState(new Set());

  useEffect(() => {
    if (open) {
      fetchTreatmentTypes();
      fetchActiveTreatments();
      
      // If we have initial context from a frame scan, pre-populate
      if (initialContext) {
        setScanContext(initialContext);
        const newFormData = { ...formData };
        
        // Pre-select the hive if provided
        if (initialContext.hive) {
          newFormData.hive_ids = [initialContext.hive.id];
          newFormData.hive_numbers = [initialContext.hive.hive_number];
          newFormData.yard_id = initialContext.hive.yard_id || '';
          newFormData.yard_name = initialContext.hive.yard || '';
        }
        
        // Add scan notes
        if (initialContext.scanResult) {
          const scanNotes = [];
          if (initialContext.scanResult.mite_visibility) {
            scanNotes.push(`AI Scan: ${initialContext.scanResult.mite_visibility} mite visibility`);
          }
          if (initialContext.scanResult.estimated_mite_load) {
            scanNotes.push(`Estimated load: ${initialContext.scanResult.estimated_mite_load}`);
          }
          if (initialContext.scanResult.recommendations?.length > 0) {
            scanNotes.push(`Recommendations: ${initialContext.scanResult.recommendations.join(', ')}`);
          }
          newFormData.notes = scanNotes.join('\n');
        }
        
        setFormData(newFormData);
        
        // If urgency is high, skip to step 2 (treatment selection) if hive is pre-selected
        if (initialContext.urgency === 'high' && initialContext.hive) {
          setStep(1);
        }
      }
    }
  }, [open, initialContext]);

  const fetchTreatmentTypes = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [res, rotRes] = await Promise.all([
        axios.get(`${API}/api/treatment-types`, { headers }),
        axios.get(`${API}/api/treatment-plans/rotation-analysis`, { headers }).catch(() => ({ data: null }))
      ]);
      setTreatmentTypes(res.data.items || []);
      setRotationRec(rotRes.data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveTreatments = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/api/treatment-plans`, { headers });
      const plans = res.data.plans || [];
      const activeIds = new Set();
      plans.forEach(p => {
        if (p.status === 'active' || p.status === 'scheduled') {
          (p.hive_ids || []).forEach(id => activeIds.add(id));
        }
      });
      setActiveTreatmentHiveIds(activeIds);
    } catch (err) { /* ignore */ }
  };


  const filteredTreatments = treatmentTypes.filter(t => 
    t.mite_types?.includes(formData.mite_type) || t.mite_types?.includes('both')
  );

  const handleTreatmentSelect = (treatmentId) => {
    const treatment = treatmentTypes.find(t => t.id === treatmentId);
    setSelectedTreatment(treatment);
    setFormData(prev => ({
      ...prev,
      treatment_type_id: treatmentId,
      name: `${treatment?.name} Treatment`
    }));
  };

  const handleHiveToggle = (hive) => {
    const isSelected = formData.hive_ids.includes(hive.id);
    if (isSelected) {
      setFormData(prev => ({
        ...prev,
        hive_ids: prev.hive_ids.filter(id => id !== hive.id),
        hive_numbers: prev.hive_numbers.filter(n => n !== hive.hive_number)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        hive_ids: [...prev.hive_ids, hive.id],
        hive_numbers: [...prev.hive_numbers, hive.hive_number]
      }));
    }
  };

  const handleYardSelect = (yardId) => {
    if (yardId === 'all') {
      setFormData(prev => ({ ...prev, yard_id: '', yard_name: '' }));
    } else {
      const yard = yards.find(y => y.id === yardId);
      setFormData(prev => ({
        ...prev,
        yard_id: yardId,
        yard_name: yard?.name || ''
      }));
    }
  };

  const handleSelectAllInYard = () => {
    const yardHives = formData.yard_id 
      ? hives.filter(h => h.yard_id === formData.yard_id || h.yard === formData.yard_name)
      : hives;
    
    setFormData(prev => ({
      ...prev,
      hive_ids: yardHives.map(h => h.id),
      hive_numbers: yardHives.map(h => h.hive_number)
    }));
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const miteCount = formData.pre_treatment_mite_count ? parseInt(formData.pre_treatment_mite_count) : null;
      const beeSample = parseInt(formData.pre_treatment_bee_sample) || 300;
      const mitePercentage = miteCount !== null ? ((miteCount / beeSample) * 100) : null;
      const payload = {
        ...formData,
        pre_treatment_mite_count: miteCount,
        pre_treatment_bee_sample: beeSample,
        pre_treatment_mite_percentage: mitePercentage ? parseFloat(mitePercentage.toFixed(1)) : null
      };
      
      const { queued } = await sendOrQueue({ path: '/treatment-plans', body: payload, kind: 'treatment_plan', label: `Treatment plan · ${selectedTreatment?.name || formData.name || 'new'}`, hiveIds: formData.hive_ids || [] });
      if (queued) queuedToast('treatment plan'); else toast.success('Treatment plan created successfully!');
      onSuccess?.({ treatment_type_name: selectedTreatment?.name || formData.name });
      onClose();
      resetForm();
    } catch (err) {
      toast.error('Failed to create treatment plan');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setStep(0);
    setScanContext(null);
    setFormData({
      mite_type: 'varroa',
      treatment_type_id: '',
      name: '',
      description: '',
      hive_ids: [],
      hive_numbers: [],
      yard_id: '',
      yard_name: '',
      start_date: new Date().toISOString().split('T')[0],
      pre_treatment_mite_count: '',
      pre_treatment_bee_sample: '300',
      notes: ''
    });
    setSelectedTreatment(null);
  };

  const canProceed = () => {
    switch (step) {
      case 0: return formData.mite_type;
      case 1: return formData.treatment_type_id;
      case 2: return formData.hive_ids.length > 0;
      case 3: return formData.start_date;
      default: return true;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'organic': return 'bg-green-100 text-green-800';
      case 'chemical': return 'bg-red-100 text-red-800';
      case 'mechanical': return 'bg-blue-100 text-blue-800';
      case 'cultural': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredHives = formData.yard_id 
    ? hives.filter(h => h.yard_id === formData.yard_id || h.yard === formData.yard_name)
    : hives;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5 text-red-600" />
            Mite Treatment Plan Wizard
          </DialogTitle>
          <DialogDescription>
            Create a comprehensive treatment plan with automated scheduling and reminders
          </DialogDescription>
        </DialogHeader>

        {/* Scan Context Banner */}
        {scanContext && (
          <div className={`p-3 rounded-lg border-2 ${
            scanContext.urgency === 'high' 
              ? 'bg-red-50 border-red-300' 
              : 'bg-amber-50 border-amber-300'
          }`}>
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded-full ${scanContext.urgency === 'high' ? 'bg-red-100' : 'bg-amber-100'}`}>
                <Sparkles className={`h-4 w-4 ${scanContext.urgency === 'high' ? 'text-red-600' : 'text-amber-600'}`} />
              </div>
              <div className="flex-1">
                <span className={`text-sm font-medium ${scanContext.urgency === 'high' ? 'text-red-800' : 'text-amber-800'}`}>
                  AI Frame Scan Detected Mites
                </span>
                {scanContext.hive && (
                  <span className="text-sm text-gray-600 ml-2">
                    • Hive #{scanContext.hive.hive_number} pre-selected
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6 px-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                ${i < step ? 'bg-green-500 text-white' : i === step ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {i < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-12 h-1 mx-1 ${i < step ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="min-h-[300px]">
          {/* Step 1: Mite Type Selection */}
          {step === 0 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">What type of mite are you treating?</h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setFormData(prev => ({ ...prev, mite_type: 'varroa' }))}
                  className={`p-6 rounded-xl border-2 transition-all text-left
                    ${formData.mite_type === 'varroa' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}
                >
                  <div className="text-3xl mb-2">🦟</div>
                  <h4 className="font-bold text-lg">Varroa destructor</h4>
                  <p className="text-sm text-gray-600 mt-1">Most common honey bee parasite worldwide</p>
                  <div className="mt-3 flex flex-wrap gap-1">
                    <Badge variant="outline" className="text-xs">Global threat</Badge>
                    <Badge variant="outline" className="text-xs">Multiple treatments</Badge>
                  </div>
                </button>
                
                <button
                  onClick={() => setFormData(prev => ({ ...prev, mite_type: 'tropilaelaps' }))}
                  className={`p-6 rounded-xl border-2 transition-all text-left
                    ${formData.mite_type === 'tropilaelaps' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}
                >
                  <div className="text-3xl mb-2">🔴</div>
                  <h4 className="font-bold text-lg">Tropilaelaps</h4>
                  <p className="text-sm text-gray-600 mt-1">Fast-reproducing Asian mite species</p>
                  <div className="mt-3 flex flex-wrap gap-1">
                    <Badge variant="outline" className="text-xs">Asia/Pacific</Badge>
                    <Badge variant="outline" className="text-xs">Brood breaks effective</Badge>
                  </div>
                </button>
              </div>
              
              <div className="p-4 bg-blue-50 rounded-lg mt-4">
                <div className="flex gap-2">
                  <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Not sure which mite?</p>
                    <p className="mt-1">Varroa mites are reddish-brown, flat, and visible on adult bees. Tropilaelaps are smaller, lighter colored, and move faster. Both can be confirmed through brood inspection.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Treatment Selection */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Select your treatment method</h3>
              
              {/* Rotation Recommendation Banner */}
              {rotationRec?.recommendation && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3" data-testid="wizard-rotation-rec">
                  <Sparkles className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-0.5">Recommended for you</div>
                    <span className="text-sm font-semibold text-stone-800">{rotationRec.recommendation.name}</span>
                    {rotationRec.recommendation.reason && (
                      <p className="text-xs text-amber-600 mt-0.5">{rotationRec.recommendation.reason}</p>
                    )}
                    {rotationRec.warnings?.length > 0 && (
                      <p className="text-xs text-red-600 mt-1 font-medium">
                        {rotationRec.warnings[0].title}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleTreatmentSelect(rotationRec.recommendation.id); setStep(3); }}
                    className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg font-medium flex-shrink-0 transition-colors"
                  >
                    Select
                  </button>
                </div>
              )}
              
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading treatments...</div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                  {['organic', 'chemical', 'mechanical', 'cultural'].map(category => {
                    const categoryTreatments = filteredTreatments.filter(t => t.category === category);
                    if (categoryTreatments.length === 0) return null;
                    
                    return (
                      <div key={category}>
                        <h4 className="text-sm font-medium text-gray-500 uppercase mb-2 sticky top-0 bg-white py-1">
                          {category} Treatments
                        </h4>
                        {categoryTreatments.map(treatment => (
                          <button
                            key={treatment.id}
                            onClick={() => handleTreatmentSelect(treatment.id)}
                            className={`w-full p-4 rounded-lg border-2 mb-2 text-left transition-all
                              ${formData.treatment_type_id === treatment.id ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-300'}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h5 className="font-semibold">{treatment.name}</h5>
                                  <Badge className={getCategoryColor(treatment.category)}>
                                    {treatment.category}
                                  </Badge>
                                </div>
                                {treatment.active_ingredient && treatment.active_ingredient !== 'None' && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    Active: {treatment.active_ingredient}
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {treatment.duration_days} days
                                  </span>
                                  <span>{treatment.applications_required} application(s)</span>
                                  {treatment.efficacy_percent && (
                                    <span className="text-green-600">{treatment.efficacy_percent} efficacy</span>
                                  )}
                                  {treatment.honey_super_safe && (
                                    <Badge variant="outline" className="text-green-600 border-green-300">Honey safe</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {formData.treatment_type_id === treatment.id && (
                              <div className="mt-3 pt-3 border-t border-amber-200 text-sm space-y-3">
                                {/* Resistance Warning */}
                                {treatment.resistance_risk && treatment.resistance_risk !== 'none' && (
                                  <div className={`p-2.5 rounded-lg flex items-start gap-2 ${
                                    treatment.resistance_risk === 'high' 
                                      ? 'bg-red-100 border border-red-300' 
                                      : 'bg-orange-50 border border-orange-300'
                                  }`}>
                                    <Shield className={`h-4 w-4 flex-shrink-0 mt-0.5 ${treatment.resistance_risk === 'high' ? 'text-red-600' : 'text-orange-600'}`} />
                                    <div>
                                      <span className={`font-bold text-xs uppercase ${treatment.resistance_risk === 'high' ? 'text-red-700' : 'text-orange-700'}`}>
                                        {treatment.resistance_risk === 'high' ? 'HIGH RESISTANCE RISK' : 'MODERATE RESISTANCE RISK'}
                                      </span>
                                      <p className={`text-xs mt-0.5 ${treatment.resistance_risk === 'high' ? 'text-red-600' : 'text-orange-600'}`}>
                                        {treatment.resistance_risk === 'high' 
                                          ? 'Mites in many regions are resistant to this treatment. Consider organic alternatives.'
                                          : 'Resistance is developing in some areas. Rotate with other treatment types yearly.'}
                                      </p>
                                    </div>
                                  </div>
                                )}
                                
                                <p className="text-gray-700">{treatment.notes}</p>
                                
                                {treatment.warnings?.length > 0 && (
                                  <div className="p-2 bg-red-50 rounded flex gap-2">
                                    <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                    <div className="text-red-700 text-xs">
                                      {treatment.warnings.join(' • ')}
                                    </div>
                                  </div>
                                )}
                                {(treatment.temp_min_c || treatment.temp_max_c) && (
                                  <div className="flex items-center gap-1 text-blue-600">
                                    <Thermometer className="h-4 w-4" />
                                    <span>
                                      Temp: {treatment.temp_min_c || '?'}°C - {treatment.temp_max_c || '?'}°C
                                    </span>
                                  </div>
                                )}

                                {/* Expandable How-To Guide */}
                                {treatment.guide && (
                                  <TreatmentGuide guide={treatment.guide} treatmentName={treatment.name} />
                                )}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Hive Selection */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Select hives to treat</h3>
              
              <div className="flex items-center gap-3 mb-4">
                <Select value={formData.yard_id || 'all'} onValueChange={handleYardSelect}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter by apiary" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Apiaries</SelectItem>
                    {yards.map(yard => (
                      <SelectItem key={yard.id} value={yard.id}>{yard.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button variant="outline" size="sm" onClick={handleSelectAllInYard}>
                  Select All ({filteredHives.length})
                </Button>
                
                {formData.hive_ids.length > 0 && (
                  <Badge className="bg-amber-100 text-amber-800">
                    {formData.hive_ids.length} selected
                  </Badge>
                )}
              </div>
              
              <div className="grid grid-cols-4 gap-2 max-h-[300px] overflow-y-auto">
                {filteredHives.map(hive => {
                  const underTreatment = activeTreatmentHiveIds.has(hive.id);
                  const isSelected = formData.hive_ids.includes(hive.id);
                  return (
                  <button
                    key={hive.id}
                    onClick={() => handleHiveToggle(hive)}
                    className={`p-3 rounded-lg border-2 text-center transition-all relative
                      ${isSelected 
                        ? 'border-amber-500 bg-amber-50' 
                        : underTreatment
                          ? 'border-green-300 bg-green-50/50 opacity-60'
                          : 'border-gray-200 hover:border-amber-300'}`}
                  >
                    {underTreatment && (
                      <div className="absolute top-1 right-1 bg-green-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold leading-none">
                        Treating
                      </div>
                    )}
                    <div className="text-xl mb-1">🐝</div>
                    <div className="font-bold">#{hive.hive_number}</div>
                    <div className="text-xs text-gray-500 truncate">{hive.yard}</div>
                  </button>
                  );
                })}
              </div>
              
              {filteredHives.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No hives found. Add hives first before creating a treatment plan.
                </div>
              )}
            </div>
          )}

          {/* Step 4: Schedule */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Set your treatment schedule</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Pre-Treatment Mite Count</Label>
                  <div className="flex gap-2 mt-1">
                    <div className="flex-1">
                      <Input
                        type="number"
                        min="0"
                        placeholder="Mites found"
                        value={formData.pre_treatment_mite_count}
                        onChange={(e) => setFormData(prev => ({ ...prev, pre_treatment_mite_count: e.target.value }))}
                      />
                      <span className="text-xs text-stone-400 mt-0.5 block">Number of mites</span>
                    </div>
                    <div className="w-24">
                      <Input
                        type="number"
                        min="1"
                        placeholder="300"
                        value={formData.pre_treatment_bee_sample}
                        onChange={(e) => setFormData(prev => ({ ...prev, pre_treatment_bee_sample: e.target.value }))}
                      />
                      <span className="text-xs text-stone-400 mt-0.5 block">Bee sample</span>
                    </div>
                  </div>
                  {formData.pre_treatment_mite_count && (
                    <div className="mt-1.5">
                      {(() => {
                        const pct = (parseInt(formData.pre_treatment_mite_count) / (parseInt(formData.pre_treatment_bee_sample) || 300)) * 100;
                        const color = pct < 2 ? 'text-green-600' : pct < 4 ? 'text-amber-600' : 'text-red-600';
                        const label = pct < 2 ? 'Low' : pct < 4 ? 'Moderate' : 'High';
                        return <span className={`text-sm font-medium ${color}`}>{pct.toFixed(1)}% — {label} mite load</span>;
                      })()}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <Label>Plan Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Fall 2025 Varroa Treatment"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional notes about this treatment..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              
              {selectedTreatment && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Generated Schedule Preview
                  </h4>
                  <div className="space-y-2 text-sm">
                    {Array.from({ length: selectedTreatment.applications_required }).map((_, i) => {
                      const date = new Date(formData.start_date);
                      date.setDate(date.getDate() + (i * (selectedTreatment.interval_days || 0)));
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-amber-500 rounded-full" />
                          <span className="font-medium">
                            {date.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                          <span className="text-gray-600">- Apply {selectedTreatment.name} (#{i + 1})</span>
                        </div>
                      );
                    })}
                    {selectedTreatment.removal_required && (
                      <div className="flex items-center gap-2 text-red-600">
                        <div className="w-2 h-2 bg-red-500 rounded-full" />
                        <span className="font-medium">
                          {new Date(new Date(formData.start_date).getTime() + selectedTreatment.removal_after_days * 86400000)
                            .toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <span>- Remove treatment</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-green-600">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      <span className="font-medium">
                        {new Date(new Date(formData.start_date).getTime() + (selectedTreatment.duration_days + 7) * 86400000)
                          .toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                      <span>- Post-treatment mite count</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Review */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Review your treatment plan</h3>
              
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Mite Type:</span>
                  <span className="font-medium capitalize">{formData.mite_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Treatment:</span>
                  <span className="font-medium">{selectedTreatment?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Hives:</span>
                  <span className="font-medium">{formData.hive_ids.length} hives selected</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Start Date:</span>
                  <span className="font-medium">{formData.start_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Duration:</span>
                  <span className="font-medium">{selectedTreatment?.duration_days} days</span>
                </div>
                {formData.pre_treatment_mite_count && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pre-treatment mite load:</span>
                    <span className="font-medium">
                      {formData.pre_treatment_mite_count} mites / {formData.pre_treatment_bee_sample || 300} bees
                      ({((parseInt(formData.pre_treatment_mite_count) / (parseInt(formData.pre_treatment_bee_sample) || 300)) * 100).toFixed(1)}%)
                    </span>
                  </div>
                )}
              </div>
              
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium">Ready to create!</p>
                    <p>This will automatically create calendar events and reminders for all treatment applications.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-6 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => {
              if (step === 0) { onClose(); return; }
              // Skip back over hive selection if it was skipped going forward
              if (step === 3 && formData.hive_ids.length > 0 && initialContext?.hive) {
                setStep(1);
              } else {
                setStep(s => s - 1);
              }
            }}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {step === 0 ? 'Cancel' : 'Back'}
          </Button>
          
          {step < STEPS.length - 1 ? (
            <Button
              onClick={() => setStep(s => {
                const next = s + 1;
                // Skip hive selection (step 2) if hive already pre-selected from context
                if (next === 2 && formData.hive_ids.length > 0 && initialContext?.hive) {
                  return 3;
                }
                return next;
              })}
              disabled={!canProceed()}
              className="bg-amber-500 hover:bg-amber-600"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleCreate}
              disabled={creating}
              className="bg-green-600 hover:bg-green-700"
            >
              {creating ? 'Creating...' : 'Create Treatment Plan'}
              <Sparkles className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TreatmentPlanWizard;

// Expandable How-To Guide for treatment types
const TreatmentGuide = ({ guide, treatmentName }) => {
  const [expanded, setExpanded] = React.useState(false);
  
  if (!guide) return null;
  
  return (
    <div className="border border-emerald-200 rounded-lg overflow-hidden" data-testid="treatment-guide">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-emerald-50 hover:bg-emerald-100 transition-colors text-left"
        data-testid="treatment-guide-toggle"
      >
        <BookOpen className="h-4 w-4 text-emerald-600 flex-shrink-0" />
        <span className="text-sm font-semibold text-emerald-800 flex-1">How-To Guide</span>
        <ChevronDown className={`h-4 w-4 text-emerald-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      
      {expanded && (
        <div className="p-4 space-y-4 bg-white text-xs" onClick={(e) => e.stopPropagation()}>
          {/* Summary */}
          {guide.summary && (
            <div className="bg-emerald-50 rounded-lg p-3">
              <p className="text-emerald-800 text-sm leading-relaxed">{guide.summary}</p>
            </div>
          )}

          {/* Step-by-step */}
          {guide.steps?.length > 0 && (
            <div>
              <h5 className="font-bold text-stone-800 flex items-center gap-1.5 mb-2 text-sm">
                <ListChecks className="h-4 w-4 text-amber-600" /> Step-by-Step Instructions
              </h5>
              <ol className="space-y-1.5">
                {guide.steps.map((step, i) => (
                  <li key={i} className="flex gap-2 text-stone-700 leading-relaxed">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center justify-center text-[10px]">{i + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          
          {/* Equipment */}
          {guide.equipment?.length > 0 && (
            <div>
              <h5 className="font-bold text-stone-800 flex items-center gap-1.5 mb-2 text-sm">
                <Wrench className="h-4 w-4 text-blue-600" /> Equipment Needed
              </h5>
              <ul className="space-y-1">
                {guide.equipment.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-stone-700">
                    <span className="text-blue-400 mt-0.5">&#8226;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Timing & Dosage */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {guide.timing && (
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="font-bold text-blue-700 mb-1 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Timing</div>
                <p className="text-blue-800">{guide.timing}</p>
              </div>
            )}
            {guide.dosage && (
              <div className="bg-purple-50 rounded-lg p-3">
                <div className="font-bold text-purple-700 mb-1">Dosage</div>
                <p className="text-purple-800">{guide.dosage}</p>
              </div>
            )}
          </div>

          {/* When to treat */}
          {guide.when_to_treat && (
            <div className="bg-amber-50 rounded-lg p-3">
              <div className="font-bold text-amber-700 mb-1 flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Best Time to Use</div>
              <p className="text-amber-800">{guide.when_to_treat}</p>
            </div>
          )}
          
          {/* Important Reminders */}
          {guide.reminders?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <h5 className="font-bold text-red-700 flex items-center gap-1.5 mb-2 text-sm">
                <BellRing className="h-4 w-4" /> Important Reminders
              </h5>
              <ul className="space-y-1.5">
                {guide.reminders.map((reminder, i) => (
                  <li key={i} className="flex items-start gap-2 text-red-700">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>{reminder}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
